import React, { useState } from 'react';
import ImageUpload from '../ImageUpload';
import { addService, updateService, deleteService } from '../../../services/dataService';

const YogaAdmin = ({ siteData, updateSiteData, setSiteData, refreshCollectionData, showSaveStatus }) => {
    const [saving, setSaving] = useState(false);
    const [editingServices, setEditingServices] = useState({});

    const handleAddService = async () => {
        try {
            setSaving(true);
            const newService = {
                title: 'New Yoga Service',
                description: '',
                image: '',
                imagePath: '',
                icon: '🧘',
                duration: '',
                price: '',
                priceType: 'flat', // 'flat', 'per-person', 'tiered', or 'inquire'
                baseCapacity: 10,
                additionalPrice: ''
            };

            const serviceId = await addService(newService);
            const serviceWithId = { ...newService, id: serviceId };

            setSiteData(prev => ({
                ...prev,
                yoga: {
                    ...prev.yoga,
                    services: [...(prev.yoga.services || []), serviceWithId]
                }
            }));

            showSaveStatus('Service added successfully!');
        } catch (error) {
            console.error('Failed to add service:', error);
            showSaveStatus('Failed to add service', true);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteService = async (service) => {
        if (window.confirm('Are you sure you want to delete this service?')) {
            try {
                setSaving(true);
                await deleteService(service.id, service.imagePath);

                // Update local state directly instead of refreshing
                const updatedServices = siteData.yoga.services.filter(s => s.id !== service.id);
                setSiteData(prev => ({
                    ...prev,
                    yoga: {
                        ...prev.yoga,
                        services: updatedServices
                    }
                }));

                showSaveStatus('Service deleted successfully!');
            } catch (error) {
                console.error('Failed to delete service:', error);
                showSaveStatus('Failed to delete service', true);
            } finally {
                setSaving(false);
            }
        }
    };

    const startEditing = (serviceId) => {
        const service = siteData.yoga.services.find(s => s.id === serviceId);
        setEditingServices(prev => ({
            ...prev,
            [serviceId]: { ...service }
        }));
    };

    const cancelEditing = (serviceId) => {
        setEditingServices(prev => {
            const newEditing = { ...prev };
            delete newEditing[serviceId];
            return newEditing;
        });
    };

    const handleFieldChange = (serviceId, field, value) => {
        setEditingServices(prev => ({
            ...prev,
            [serviceId]: {
                ...prev[serviceId],
                [field]: value
            }
        }));
    };

    const saveService = async (serviceId) => {
        try {
            setSaving(true);
            const updatedService = editingServices[serviceId];

            await updateService(serviceId, updatedService);

            // Update local state directly
            const updatedServices = siteData.yoga.services.map(s =>
                s.id === serviceId ? updatedService : s
            );

            setSiteData(prev => ({
                ...prev,
                yoga: {
                    ...prev.yoga,
                    services: updatedServices
                }
            }));

            // Clear from editing
            setEditingServices(prev => {
                const newEditing = { ...prev };
                delete newEditing[serviceId];
                return newEditing;
            });

            showSaveStatus('Service updated successfully!');
        } catch (error) {
            console.error('Failed to update service:', error);
            showSaveStatus('Failed to update service', true);
        } finally {
            setSaving(false);
        }
    };

    const handleImageUpload = async (serviceId, field, pathField, imageUrl, imagePath) => {
        try {
            const serviceToUpdate = siteData.yoga.services.find(s => s.id === serviceId);
            const updatedService = {
                ...serviceToUpdate,
                [field]: imageUrl,
                [pathField]: imagePath
            };

            await updateService(serviceId, updatedService);

            // Update local state directly instead of using refreshCollectionData
            const updatedServices = siteData.yoga.services.map(s =>
                s.id === serviceId ? updatedService : s
            );

            setSiteData(prev => ({
                ...prev,
                yoga: {
                    ...prev.yoga,
                    services: updatedServices
                }
            }));

            showSaveStatus('Service image updated successfully!');
        } catch (error) {
            console.error('Failed to update service image:', error);
            showSaveStatus('Failed to update service image', true);
        }
    };

    const updateYogaData = async (updates) => {
        try {
            const updatedYoga = {
                ...siteData.yoga,
                ...updates
            };

            setSiteData(prev => ({
                ...prev,
                yoga: updatedYoga
            }));

            await updateSiteData('yoga', updatedYoga);
        } catch (error) {
            console.error('Failed to update yoga:', error);
            showSaveStatus('Failed to update yoga section', true);
        }
    };

    const isEditing = (serviceId) => !!editingServices[serviceId];
    const getServiceData = (serviceId) => {
        return editingServices[serviceId] || siteData.yoga.services.find(s => s.id === serviceId);
    };

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Yoga Services</h2>
                <button
                    onClick={handleAddService}
                    disabled={saving}
                    className="bg-sage-600 text-white px-4 py-2 rounded-lg hover:bg-sage-700 transition-colors disabled:opacity-50"
                >
                    {saving ? 'Adding...' : 'Add Service'}
                </button>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                <input
                    type="text"
                    value={siteData.yoga.title || ''}
                    onChange={(e) => updateYogaData({ title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sage-500 focus:border-transparent"
                    placeholder="Yoga Services"
                />
            </div>

            <div className="space-y-6">
                {siteData.yoga.services?.map((service) => {
                    const serviceData = getServiceData(service.id);
                    const editing = isEditing(service.id);

                    return (
                        <div key={service.id} className="border border-gray-200 rounded-lg p-6">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-lg font-medium text-gray-900">Service: {service.title}</h3>
                                <div className="flex space-x-2">
                                    {editing ? (
                                        <>
                                            <button
                                                onClick={() => saveService(service.id)}
                                                disabled={saving}
                                                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                                            >
                                                {saving ? 'Saving...' : 'Save'}
                                            </button>
                                            <button
                                                onClick={() => cancelEditing(service.id)}
                                                disabled={saving}
                                                className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 disabled:opacity-50"
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={() => startEditing(service.id)}
                                            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                                        >
                                            Edit
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleDeleteService(service)}
                                        disabled={saving}
                                        className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Service Title</label>
                                        <input
                                            type="text"
                                            value={serviceData.title}
                                            onChange={(e) => editing ? handleFieldChange(service.id, 'title', e.target.value) : null}
                                            disabled={!editing}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500 ${!editing ? 'bg-gray-100 cursor-not-allowed' : ''
                                                }`}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Icon (emoji)</label>
                                        <input
                                            type="text"
                                            value={serviceData.icon}
                                            onChange={(e) => editing ? handleFieldChange(service.id, 'icon', e.target.value) : null}
                                            disabled={!editing}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500 ${!editing ? 'bg-gray-100 cursor-not-allowed' : ''
                                                }`}
                                            placeholder="🧘"
                                        />
                                    </div>

                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
                                                <input
                                                    type="text"
                                                    value={serviceData.duration}
                                                    onChange={(e) => editing ? handleFieldChange(service.id, 'duration', e.target.value) : null}
                                                    disabled={!editing}
                                                    className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500 ${!editing ? 'bg-gray-100 cursor-not-allowed' : ''
                                                        }`}
                                                    placeholder="60 minutes"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Pricing Type</label>
                                                <select
                                                    value={serviceData.priceType || 'flat'}
                                                    onChange={(e) => editing ? handleFieldChange(service.id, 'priceType', e.target.value) : null}
                                                    disabled={!editing}
                                                    className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500 ${!editing ? 'bg-gray-100 cursor-not-allowed' : ''
                                                        }`}
                                                >
                                                    <option value="flat">Flat Fee</option>
                                                    <option value="per-person">Per Person</option>
                                                    <option value="tiered">Tiered (Base + Additional)</option>
                                                    <option value="inquire">Contact for Pricing</option>
                                                </select>
                                            </div>
                                        </div>

                                        {serviceData.priceType !== 'inquire' && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        {serviceData.priceType === 'tiered' ? 'Base Price' : 'Price'}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={serviceData.price}
                                                        onChange={(e) => editing ? handleFieldChange(service.id, 'price', e.target.value) : null}
                                                        disabled={!editing}
                                                        className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500 ${!editing ? 'bg-gray-100 cursor-not-allowed' : ''
                                                            }`}
                                                        placeholder="200"
                                                    />
                                                </div>

                                                {serviceData.priceType === 'tiered' && (
                                                    <>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Base Capacity (number of people included)
                                                            </label>
                                                            <input
                                                                type="number"
                                                                value={serviceData.baseCapacity || 10}
                                                                onChange={(e) => editing ? handleFieldChange(service.id, 'baseCapacity', e.target.value) : null}
                                                                disabled={!editing}
                                                                className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500 ${!editing ? 'bg-gray-100 cursor-not-allowed' : ''
                                                                    }`}
                                                                placeholder="10"
                                                                min="1"
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Price per Additional Guest
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={serviceData.additionalPrice || ''}
                                                                onChange={(e) => editing ? handleFieldChange(service.id, 'additionalPrice', e.target.value) : null}
                                                                disabled={!editing}
                                                                className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500 ${!editing ? 'bg-gray-100 cursor-not-allowed' : ''
                                                                    }`}
                                                                placeholder="22"
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                        <textarea
                                            value={serviceData.description}
                                            onChange={(e) => editing ? handleFieldChange(service.id, 'description', e.target.value) : null}
                                            disabled={!editing}
                                            rows={4}
                                            className={`w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500 ${!editing ? 'bg-gray-100 cursor-not-allowed' : ''
                                                }`}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Service Image</label>
                                    <ImageUpload
                                        currentImage={service.image}
                                        currentImagePath={service.imagePath}
                                        onImageUpload={(imageUrl, imagePath) =>
                                            handleImageUpload(service.id, 'image', 'imagePath', imageUrl, imagePath)
                                        }
                                        label="Upload Service Image"
                                        folder={`yoga/${service.id}`}
                                        aspectRatio={4 / 3}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}

                {(!siteData.yoga.services || siteData.yoga.services.length === 0) && (
                    <div className="text-center py-12 text-gray-500">
                        <p>No services added yet. Click "Add Service" to get started.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default YogaAdmin;